import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { MapPin, Phone, Instagram, Clock, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SalonPage = () => {
  const [salonInfo, setSalonInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSalonInfo();
  }, []);

  const fetchSalonInfo = async () => {
    try {
      const response = await axios.get(`${API}/salon-info`);
      setSalonInfo(response.data);
    } catch (error) {
      toast.error('Errore nel caricamento delle informazioni');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-12 w-12 animate-spin text-accent" />
      </div>
    );
  }

  return (
    <div className="min-h-screen py-20 container mx-auto px-4">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <h1 className="text-4xl sm:text-5xl font-bold mb-4">Il Nostro Salone</h1>
          <p className="text-muted-foreground text-lg">Scopri chi siamo e dove trovarci</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
          {/* About Card */}
          <Card className="card-elegant">
            <CardHeader>
              <CardTitle className="text-2xl">Chi Siamo</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground leading-relaxed">
                {salonInfo?.description || 'Informazioni in arrivo...'}
              </p>
            </CardContent>
          </Card>

          {/* Opening Hours Card */}
          <Card className="card-elegant">
            <CardHeader>
              <CardTitle className="text-2xl flex items-center gap-2">
                <Clock className="h-6 w-6 text-accent" />
                Orari di Apertura
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {salonInfo?.opening_hours ? (
                  Object.entries(salonInfo.opening_hours).map(([day, hours]) => (
                    <div key={day} className="flex justify-between items-center">
                      <span className="font-medium">{day}</span>
                      <span className={`text-muted-foreground ${hours === 'Chiuso' ? 'text-destructive' : ''}`}>
                        {hours}
                      </span>
                    </div>
                  ))
                ) : (
                  <p className="text-muted-foreground">Orari da definire</p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Contact Info */}
        <Card className="card-elegant mb-12">
          <CardHeader>
            <CardTitle className="text-2xl">Contatti</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
              <div className="flex items-start gap-3">
                <div className="p-3 rounded-lg bg-accent/10">
                  <MapPin className="h-6 w-6 text-accent" />
                </div>
                <div>
                  <h3 className="font-semibold mb-1">Indirizzo</h3>
                  <p className="text-sm text-muted-foreground">{salonInfo?.address || 'Da configurare'}</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="p-3 rounded-lg bg-accent/10">
                  <Phone className="h-6 w-6 text-accent" />
                </div>
                <div>
                  <h3 className="font-semibold mb-1">Telefono</h3>
                  <a 
                    href={`tel:${salonInfo?.phone}`} 
                    className="text-sm text-muted-foreground hover:text-accent transition-colors"
                  >
                    {salonInfo?.phone || 'Da configurare'}
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="p-3 rounded-lg bg-accent/10">
                  <Instagram className="h-6 w-6 text-accent" />
                </div>
                <div>
                  <h3 className="font-semibold mb-1">Instagram</h3>
                  <a 
                    href="https://instagram.com" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-sm text-muted-foreground hover:text-accent transition-colors"
                  >
                    {salonInfo?.instagram || '@capelliecapelli'}
                  </a>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Map */}
        {salonInfo?.map_url ? (
          <Card className="card-elegant">
            <CardHeader>
              <CardTitle className="text-2xl">Come Raggiungerci</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="aspect-video rounded-lg overflow-hidden">
                <iframe
                  src={salonInfo.map_url}
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Mappa del salone"
                />
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card className="card-elegant">
            <CardContent className="p-12 text-center">
              <MapPin className="h-16 w-16 text-muted-foreground mx-auto mb-4 opacity-50" />
              <h3 className="text-xl font-semibold mb-2">Mappa in Arrivo</h3>
              <p className="text-muted-foreground">La mappa verrà aggiunta a breve</p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default SalonPage;
